<template>
  <div class="relative">
    <TransitionRoot appear :show="show" as="template">
      <Dialog as="div" @close="close" class="relative z-[999] w-full">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-dark-blue-light" aria-hidden="true" />
        </TransitionChild>

        <div class="fixed inset-0">
          <div class="flex min-h-full items-center justify-center text-center w-full pt-10 md:pt-0">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel
                id="modal"
                class="w-full max-w-[357px] md:max-w-[952px] max-h-[85vh] md:max-h-[627px] h-auto transform overflow-y-auto bg-white text-left align-middle shadow-xl transition-all"
              >
                <button
                  @click="close"
                  type="button"
                  class="absolute md:mr-6 md:mt-6 top-4 right-4 z-50 text-gray-400 bg-light-silver rounded-full fill-secondary p-1"
                >
                  <svg aria-hidden="true" class="w-5 h-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="sr-only">Close modal</span>
                </button>

                <div class="w-full max-w-[323px] md:max-w-[864px] mx-auto md:pt-12 pt-16 pb-12 relative px-4 md:px-0">
                  <div class="w-full mb-10 md:mb-12">
                    <h3 class="text-xl md:text-2xl font-bold text-space-cadet leading-tight">
                      {{ $t("clinic.modal.title") }}
                    </h3>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-[128px]">
                    <!-- Clinic Column -->
                    <div class="flex flex-col">
                      <div class="flex items-center gap-3">
                        <NuxtImg loading="lazy" src="/icons/clinic-icon.svg" alt="clinic icon" width="36" height="36" />
                        <div class="flex items-center">
                          <IconsMarkerPin color="#991e66" width="12" height="16" />
                          <span class="text-space-cadet font-bold text-lg pl-1">
                            {{ $t("clinic.modal.clinicTitle") }}
                          </span>
                        </div>
                      </div>
                      <p class="text-[16px] leading-[28px] text-space-cadet ml-[49px] pt-2">
                        {{ $t("clinic.modal.clinicContent") }}
                      </p>
                    </div>

                    <!-- Nurse Station Column -->
                    <div class="flex flex-col">
                      <div class="flex items-center gap-4">
                        <NuxtImg
                          loading="lazy"
                          src="/icons/nurse-icon.svg"
                          alt="nurse station icon"
                          width="36"
                          height="36"
                        />
                        <div class="flex items-center">
                          <IconsMarkerPin color="#01b2a8" width="12" height="16" />
                          <span class="text-space-cadet font-bold text-lg pl-1">
                            {{ $t("clinic.modal.nurseTitle") }}
                          </span>
                        </div>
                      </div>
                      <p class="text-[16px] leading-[28px] text-space-cadet ml-[49px] pt-2">
                        {{ $t("clinic.modal.nurseContent") }}
                      </p>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";

interface ModelProp {
  show: boolean;
}

const props = withDefaults(defineProps<ModelProp>(), {
  show: false,
});

const emit = defineEmits(["close"]);

const close = () => {
  emit("close");
};
</script>
